import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ISearchResult, searchQuery } from "../services/search";
import { useLanguage } from "../context/LanguageContext";

function SearchBar() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ISearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const queryRef = useRef(query);
  queryRef.current = query;
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Debounced search — min 2 karakter
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const term = query.trim();
        const res = await searchQuery(term);
        // Bu arada arama gönderildiyse (kutu temizlendi) eski sonuç listeyi açmasın.
        if (queryRef.current.trim() !== term) return;
        setResults(res.data);
        setOpen(res.data.length > 0);
      } catch {
        setResults([]);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  // Dışarı tıklanınca kapat
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Buton/Enter: sonuçları restoran bazında gruplanmış sonuç sayfasında göster.
  // Gelişmiş arama en az 3 karakter istiyor.
  const handleSubmit = () => {
    const term = query.trim();
    if (term.length < 3) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setOpen(false);
    setQuery("");
    navigate(`/restaurants?q=${encodeURIComponent(term)}`);
  };

  const handleSelect = (result: ISearchResult) => {
    setOpen(false);
    setQuery("");
    navigate(`/restaurants/${result.id}`);
  };

  return (
    <div className="d-flex flex-column align-items-center mt-4 mb-3">
      <div
        ref={containerRef}
        className="position-relative"
        style={{ maxWidth: "500px", width: "100%" }}
      >
        <div className="input-group">
          <input
            type="text"
            className="form-control form-control-lg"
            style={{ borderRadius: "50px 0 0 50px" }}
            placeholder={t("search.placeholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setOpen(false);
              if (e.key === "Enter") handleSubmit();
            }}
            onFocus={() => results.length > 0 && setOpen(true)}
            autoComplete="off"
          />
          <button
            className="btn btn-danger px-4"
            style={{ borderRadius: "0 50px 50px 0" }}
            type="button"
            disabled={loading}
            onClick={handleSubmit}
          >
            {loading
              ? <span className="spinner-border spinner-border-sm"></span>
              : <i className="bi bi-search"></i>
            }
          </button>
        </div>

        {open && (
          <ul
            className="list-group shadow"
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              right: 0,
              zIndex: 1050,
              maxHeight: "320px",
              overflowY: "auto",
              borderRadius: "12px",
              border: "1px solid #e9ecef",
            }}
          >
            {results.map((r) => {
              const commaIdx = r.name.lastIndexOf(", ");
              const foodName = commaIdx !== -1 ? r.name.slice(0, commaIdx) : r.name;
              const restaurantName = commaIdx !== -1 ? r.name.slice(commaIdx + 2) : "";
              return (
                <li
                  key={r.id + r.name}
                  className="list-group-item list-group-item-action px-3 py-2"
                  style={{ cursor: "pointer", border: "none", borderBottom: "1px solid #f0f0f0" }}
                  onMouseDown={() => handleSelect(r)}
                >
                  <span className="fw-semibold small">{foodName}</span>
                  {restaurantName && (
                    <span className="text-danger small ms-1">, {restaurantName}</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export default SearchBar;
